import { useMemo } from 'react';

import { Avatar, Button, Heading, Text } from '@praetoid/ui';
import { Link, createFileRoute, useRouter } from '@tanstack/react-router';

import {
	getShowInvitationQueryOptions,
	useAcceptOrganizationInvitation,
	useShowInvitation,
	useSignOut,
	useUser
} from '@praetoid/client';

import { parseError } from '~/lib/errors';

export const Route = createFileRoute('/_auth/invites/$inviteToken')({
	beforeLoad: async ({ context: { queryClient }, params: { inviteToken } }) => {
		try {
			queryClient.ensureQueryData(getShowInvitationQueryOptions(inviteToken));
		} catch (error) {
			console.error(error);
		}
	},
	component: RouteComponent
});

function RouteComponent() {
	const router = useRouter();
	const { inviteToken } = Route.useParams();
	const { data: user } = useUser();
	const { data: invitation, error } = useShowInvitation(inviteToken);

	const { mutateAsync: acceptOrganizationInvitation } = useAcceptOrganizationInvitation();

	const { mutateAsync: signOut } = useSignOut({
		mutation: {
			onSuccess: async () => {
				await router.navigate({ to: '/signin' });
			}
		}
	});

	const isMatch = user?.email === invitation?.email;

	const state = useMemo(() => {
		const message = parseError(error);

		if (message === 'Email mismatch') {
			return {
				type: 'mismatch',
				title: 'Email mismatch',
				description:
					'This invitation has been sent to a different email address than your current account. Please verify with the sender or log out to continue with verification.'
			};
		}

		if (!invitation) {
			return {
				type: 'not_found',
				title: 'Invitation expired',
				description: 'This invitation has either expired, has been accepted, or does not exist.'
			};
		}

		return {
			type: 'accept',
			title: 'Join the team',
			description: `You have been invited to join the ${invitation.organization?.name} organization with ${invitation.role} access.`
		};
	}, [error, invitation]);

	const handleAccept = async () => {
		if (isMatch) {
			await acceptOrganizationInvitation({ token: inviteToken });
			router.navigate({
				to: '/'
			});
		} else {
			router.navigate({
				to: '/signup',
				search: {
					fullName: invitation?.full_name ?? '',
					email: invitation?.email,
					organizationName: invitation?.organization?.name
				}
			});
		}
	};

	return (
		<div className="ui-gradient grid h-screen grid-cols-[1fr_440px_1fr] grid-rows-1 p-4">
			<div className="flex h-full items-center justify-center" style={{ gridColumnStart: 2 }}>
				<div className="ui-radial-gradient flex w-full flex-col space-y-12 rounded-md border p-8">
					<div className="flex h-3.5 w-12 flex-col self-center rounded-full border border-neutral-300 bg-neutral-300/50 dark:border-white/15 dark:bg-black/90" />

					<div className="flex flex-col space-y-2">
						<Heading className="text-center text-2xl leading-9 font-semibold md:text-3xl">
							{state.title}
						</Heading>

						<Text className="text-center text-sm font-light" color="gray">
							{state.description}
						</Text>
					</div>

					{state.type === 'mismatch' && (
						<>
							<div className="bg-panel flex flex-col overflow-hidden rounded-md border">
								<div className="flex flex-col items-center gap-1 border-b p-4">
									<Avatar
										radius="full"
										size="1"
										variant="soft"
										fallback={user?.fullName?.charAt(0) ?? '?'}
									/>
									<Text>{user?.fullName}</Text>
									<Text color="gray" size="1">
										{user?.email}
									</Text>
								</div>

								<Button
									variant="ghost"
									className="m-0"
									size="3"
									radius="none"
									onClick={() => signOut()}
								>
									Logout
								</Button>
							</div>

							<Button variant="ghost" className="m-0" size="3" radius="none">
								Return Home
							</Button>
						</>
					)}

					{state.type === 'accept' && (
						<div className="flex flex-col space-y-4">
							<Button variant="soft" className="mx-0" size="3" onClick={handleAccept}>
								Accept Invitation
							</Button>

							<Button variant="ghost" className="mx-0" size="3">
								<Link to="/">Maybe later</Link>
							</Button>
						</div>
					)}
				</div>
			</div>
		</div>
	);
}
